import { ClipboardCheck, Copy, Loader2 } from "lucide-react"
import { FC, useState } from "react"
import { useGetIntegrationDetailsQuery } from "../../../../redux/queries"

const EMBED_DOCS_URL =
  "https://bankingbridgesupport.zendesk.com/hc/en-us/articles/33152367547159-bankingbridge-embed"
const DEVELOPER_EMAIL_SUBJECT =
  "BankingBridge install instructions for developer"
const UNAVAILABLE_SNIPPET_TEXT = "Snippet unavailable."

const STEPS = [
  {
    title: "Copy the code snippet",
    description: "Copy the code snippet from the code block below."
  },
  {
    title: "Paste in website's header",
    description: (
      <>
        Paste the code directly into a{" "}
        <code className="font-mono text-xs bg-violet-50 text-indigo-600 px-1 py-0.5 rounded">
          &lt;HTML&gt;
        </code>{" "}
        section of your webpage codebase. Publish changes.
      </>
    )
  },
  {
    title: "Trigger the embed",
    description: (
      <>
        Include the &ldquo;onclick&rdquo; command to trigger the embed.{" "}
        <a
          href={EMBED_DOCS_URL}
          target="_blank"
          rel="noreferrer"
          className="font-bold underline text-[#4a1a9d]"
        >
          Here is documentation
        </a>{" "}
        for supported &ldquo;onclick&rdquo; functionality.
      </>
    )
  }
]

interface Props {
  integrationId?: string
}

const buildDeveloperMailto = (snippet: string): string => {
  const subject = encodeURIComponent(DEVELOPER_EMAIL_SUBJECT)
  const body = encodeURIComponent(
    [
      "Hi,",
      "",
      "Could you help install this BankingBridge integration?",
      "",
      "Install steps:",
      "1) Copy the code snippet.",
      "2) Paste it in the website body HTML.",
      "3) Add the onclick trigger command.",
      "",
      "Snippet:",
      snippet || UNAVAILABLE_SNIPPET_TEXT,
      "",
      `Documentation: ${EMBED_DOCS_URL}`,
      "",
      "Thank you."
    ].join("\n")
  )
  return `mailto:?subject=${subject}&body=${body}`
}

const InstallStep: FC<Props> = ({ integrationId }) => {
  const [copied, setCopied] = useState(false)

  const { data: integration, isLoading } = useGetIntegrationDetailsQuery(
    integrationId!,
    { skip: !integrationId }
  )

  const snippet = integration?.app_settings?.install_script ?? ""

  const copy = () => {
    navigator.clipboard.writeText(snippet).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1800)
  }

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_420px] items-start">
      {/* Code snippet card */}
      <div className="bg-[#0f172a] rounded-lg p-7 flex flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#66d9cc] shrink-0" />
            <span className="text-[11px] font-normal uppercase tracking-[1.1px] text-white">
              Code Snippet for Installation
            </span>
          </div>
          <button
            onClick={copy}
            disabled={!snippet}
            className="flex items-center gap-2 bg-[#4a1a9d] hover:bg-[#3d1585] disabled:opacity-50 text-white text-[11px] font-semibold uppercase tracking-[0.275px] px-4 py-2 rounded transition-colors"
          >
            {copied ? <ClipboardCheck size={12} /> : <Copy size={12} />}
            {copied ? "Copied!" : "Copy Snippet"}
          </button>
        </div>

        <div className="bg-white rounded-lg px-6 py-6 overflow-x-auto min-h-[160px] flex items-center">
          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm w-full justify-center">
              <Loader2 size={16} className="animate-spin" />
              Loading snippet…
            </div>
          ) : (
            <pre className="font-mono text-[14px] leading-[1.625] whitespace-pre-wrap break-all w-full">
              {snippet ? (
                <SyntaxHighlight code={snippet} />
              ) : (
                <span className="italic text-slate-400">
                  {integrationId
                    ? "No install script available."
                    : "Integration ID missing — snippet unavailable."}
                </span>
              )}
            </pre>
          )}
        </div>
      </div>

      {/* How to Install panel */}
      <div className="bg-white rounded-lg shadow-sm p-5 flex flex-col justify-between gap-5 min-h-[360px]">
        <div className="flex flex-col gap-5">
          <h2 className="text-[18px] font-bold text-[#232227]">
            How to Install
          </h2>

          <ol className="flex flex-col gap-5">
            {STEPS.map((step, i) => (
              <li key={i} className="flex gap-4 items-start">
                <span className="w-8 h-8 rounded bg-[#260c54] flex items-center justify-center text-[14px] font-semibold text-white shrink-0">
                  {i + 1}
                </span>
                <div>
                  <p className="text-[15px] font-semibold text-[#191c1e] leading-snug mb-1">
                    {step.title}
                  </p>
                  <p className="text-[14px] text-[#474651] leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <a
          href={buildDeveloperMailto(snippet)}
          className="w-full bg-[#f1f1f1] hover:bg-neutral-200 rounded text-[16px] font-medium text-[#281d57] py-3 px-4 text-center transition-colors"
        >
          Email instructions to developer
        </a>
      </div>
    </div>
  )
}

type Segment = { text: string; className?: string }

const TOKEN_PATTERNS: { pattern: RegExp; className: string }[] = [
  { pattern: /<!--[\s\S]*?-->/g, className: "text-slate-400" },
  { pattern: /"[^"]*"/g, className: "text-[#002a26]" },
  { pattern: /\b(async|src)\b/g, className: "text-[#818cf8]" },
  { pattern: /<\/?script[^>]*>/g, className: "text-[rgba(49,46,129,0.8)]" }
]

const DEFAULT_TOKEN_CLASS = "text-[rgba(49,46,129,0.8)]"

function SyntaxHighlight({ code }: { code: string }) {
  let segments: Segment[] = [{ text: code }]

  for (const { pattern, className } of TOKEN_PATTERNS) {
    const next: Segment[] = []
    for (const seg of segments) {
      if (seg.className) {
        next.push(seg)
        continue
      }
      let last = 0
      for (const m of Array.from(seg.text.matchAll(pattern))) {
        if (m.index! > last) next.push({ text: seg.text.slice(last, m.index) })
        next.push({ text: m[0], className })
        last = m.index! + m[0].length
      }
      if (last < seg.text.length) next.push({ text: seg.text.slice(last) })
    }
    segments = next
  }

  return (
    <>
      {segments.map((seg, i) => (
        <span key={i} className={seg.className ?? DEFAULT_TOKEN_CLASS}>
          {seg.text}
        </span>
      ))}
    </>
  )
}

export default InstallStep
